/**
 * PWA Install Prompt — Rastyle Republik Kenya
 *
 * Behaviour:
 *  - Captures the `beforeinstallprompt` event (Chrome/Edge/Samsung Internet).
 *  - Only surfaces after 2nd qualifying visit (avoid annoying first-timers).
 *  - Falls back to a manual iOS Safari instruction sheet (Safari does not fire
 *    beforeinstallprompt — users must use "Add to Home Screen" themselves).
 *  - Persists "dismissed" in localStorage for 30 days.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share } from 'lucide-react';

const VISIT_KEY = 'rastyle_pwa_visits';
const DISMISS_KEY = 'rastyle_pwa_dismissed_at';
const DISMISS_DAYS = 30;
const MIN_VISITS = 2;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    // Track visits
    const visits = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10) + 1;
    localStorage.setItem(VISIT_KEY, String(visits));

    // Check dismissed window
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    if (dismissedAt && daysSince < DISMISS_DAYS) return;

    if (visits < MIN_VISITS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Delay slightly so users see the homepage first
      setTimeout(() => setShowPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setShowPrompt(false);
      setShowIOS(false);
      localStorage.removeItem(VISIT_KEY);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS fallback — no event fires, so we show the manual sheet
    if (isIOS()) {
      setTimeout(() => setShowIOS(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    } else {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowPrompt(false);
    setShowIOS(false);
  };

  if (showPrompt && deferredPrompt) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="lg:hidden fixed bottom-20 left-4 right-4 z-[60] bg-surface border border-rastyle-orange/40 rounded-2xl p-4 shadow-2xl shadow-rastyle-orange/20"
          data-testid="pwa-install-prompt"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 shrink-0 bg-black border border-rastyle-orange/40 rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-rastyle-orange/10">
              <img src="/splash_logo.png" alt="Rastyle" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-base uppercase tracking-tight">Install Rastyle KE</p>
              <p className="text-zinc-400 text-xs mt-0.5">One-tap launch · faster loads · works inside the club.</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={install}
                  className="flex-1 h-10 bg-rastyle-orange text-black font-bold uppercase tracking-wide text-xs rounded-full active:scale-95 transition-transform inline-flex items-center justify-center gap-1.5"
                  data-testid="pwa-install-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Install
                </button>
                <button
                  onClick={dismiss}
                  className="h-10 px-4 border border-white/15 text-zinc-400 text-xs uppercase tracking-wide rounded-full hover:text-white"
                  data-testid="pwa-dismiss-btn"
                >
                  Later
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-zinc-500 hover:text-white -mt-1 -mr-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (showIOS) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="lg:hidden fixed bottom-20 left-4 right-4 z-[60] bg-surface border border-rastyle-orange/40 rounded-2xl p-4"
          data-testid="pwa-ios-prompt"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 shrink-0 bg-black border border-rastyle-orange/40 rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-rastyle-orange/10">
              <img src="/splash_logo.png" alt="Rastyle" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-base uppercase tracking-tight">Add to Home Screen</p>
              <p className="text-zinc-400 text-xs mt-1">
                Tap <Share className="w-3 h-3 inline -mt-0.5" /> below, then{' '}
                <span className="text-rastyle-orange font-medium">"Add to Home Screen"</span>.
              </p>
            </div>
            <button
              onClick={dismiss}
              className="text-zinc-500 hover:text-white -mt-1 -mr-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return null;
}
